import { useCallback, useRef, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useProject } from "@/hooks/use-project";
import { useElementWidth } from "@/hooks/use-element-width";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useOnlineStatus } from "@/offline/online-status";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { IssueDetail } from "@/components/issues/issue-detail/issue-detail";
import { CollectionDetailView } from "@/components/reports/collection-detail-view/collection-detail-view";
import { useCollection } from "@/components/reports/collection-detail-view/use-collection";
import { ReportRecordViewer } from "@/components/reports/report-record-viewer/report-record-viewer";
import { useReportRecord } from "@/components/reports/report-record-viewer/use-report-record";
import {
  resolveSidePanelLayout,
  sidePanelWidth,
} from "@/lib/side-panel-layout";
import { cn } from "@/lib/utils";
import type { IssueStatus, ProjectIssue, ProjectRecord } from "@/types/api";
import type { StatusSelection } from "@/components/issues/issue-status-nav/issue-status-nav";

interface ReportDetailSearch {
  /**
   * The issue open beside an issues report (desktop) or in place of it
   * (phone). In the URL so it is shareable, survives a refresh, and Back
   * closes it. See [[report-detail-page.spec]] § Opening an issue.
   */
  issue?: string;
  /**
   * The record open beside a photos report (desktop) or over it (phone), with
   * the same history rules as `issue`. See [[report-detail-page.spec]]
   * § Opening a photo.
   */
  record?: string;
  /**
   * The status tab of an issues report, as on the Issues page: in the URL so it
   * is shareable and survives a refresh. Absent means All. See
   * [[report-detail-page.spec]] § Status tabs.
   */
  status?: IssueStatus;
}

const ISSUE_STATUSES: readonly IssueStatus[] = [
  "assigned",
  "in_progress",
  "completed",
  "closed",
];

export const Route = createFileRoute("/projects/$projectId/reports/$reportId/")({
  validateSearch: (search: Record<string, unknown>): ReportDetailSearch => ({
    issue: typeof search.issue === "string" ? search.issue : undefined,
    record: typeof search.record === "string" ? search.record : undefined,
    // Anything that is not a status is ignored, as is any status on a photos
    // report, so a stale link still opens All.
    status: ISSUE_STATUSES.find((status) => status === search.status),
  }),
  component: ReportDetailPage,
});

export function ReportDetailPage() {
  const { projectId, reportId } = useParams({
    from: "/projects/$projectId/reports/$reportId/",
  });
  const navigate = useNavigate({
    from: "/projects/$projectId/reports/$reportId/",
  });
  const search = Route.useSearch();
  const { data: project } = useProject(projectId);
  // The same cached query the view below reads — here only for the kind.
  const { data: collection } = useCollection(projectId, reportId);
  const isOnline = useOnlineStatus();

  // An issue or a photo opened from a report stays in the report, framed by
  // the Issues and Photos pages' side-panel rules — there is just no filter
  // sidebar to step aside.
  const isDesktop = useIsDesktop();
  const rootRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(rootRef);
  // Each kind opens only its own, and only while the report itself is on
  // screen: offline, loading and error states come first and stay whole.
  const isPhotos = isOnline && collection?.kind === "records";
  const openIssueId =
    isOnline && collection?.kind === "issues" ? (search.issue ?? null) : null;
  const openRecordId = isPhotos ? (search.record ?? null) : null;
  // Null until there is a record to show — a link may name one the page has
  // not loaded yet, or one that no longer exists.
  const openRecord = useReportRecord(projectId, reportId, openRecordId);
  const layout = resolveSidePanelLayout({
    isOpen: openIssueId !== null || openRecord !== null,
    isDesktop,
    availableWidth,
    sidebarWidth: 0,
  });
  const isSplit = layout === "split" || layout === "split-no-sidebar";
  // A phone, or a desktop window too narrow for the panel: the item covers
  // the report rather than sitting beside it.
  const isInPlace = layout === "dialog" || layout === "fullscreen";

  const openIssue = useCallback(
    (issue: ProjectIssue) => {
      void navigate({
        search: (prev) => ({ ...prev, issue: issue.id }),
        // Opening pushes, so Back closes it; switching between issues
        // replaces, so Back doesn't walk through every issue clicked.
        replace: !!search.issue,
      });
    },
    [navigate, search.issue],
  );

  // Replaces, as the Issues page does: a tab is a view, not a place to go
  // back to.
  const setStatus = useCallback(
    (status: StatusSelection) => {
      void navigate({
        search: (prev) => ({ ...prev, status: status ?? undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const closeIssue = useCallback(() => {
    void navigate({
      search: (prev) => ({ ...prev, issue: undefined }),
      replace: true,
    });
  }, [navigate]);

  const showRecord = useCallback(
    (recordId: string) => {
      void navigate({
        search: (prev) => ({ ...prev, record: recordId }),
        // As for issues: opening pushes, switching replaces.
        replace: !!search.record,
      });
    },
    [navigate, search.record],
  );

  // The report beside the panel can swap the open record while it is being
  // edited; ask before that discards the edit, as the Photos page does.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingRecordId, setPendingRecordId] = useState<string | null>(null);
  const openRecordFromReport = useCallback(
    (record: ProjectRecord) => {
      if (
        isSplit &&
        hasUnsavedChanges &&
        openRecord &&
        record.id !== openRecord.id
      ) {
        setPendingRecordId(record.id);
        return;
      }
      showRecord(record.id);
    },
    [isSplit, hasUnsavedChanges, openRecord, showRecord],
  );

  // Previous/next replace, so Back closes the viewer rather than stepping back
  // through every photo it passed.
  const stepToRecord = useCallback(
    (record: ProjectRecord) => {
      void navigate({
        search: (prev) => ({ ...prev, record: record.id }),
        replace: true,
      });
    },
    [navigate],
  );

  const closeRecord = useCallback(() => {
    void navigate({
      search: (prev) => ({ ...prev, record: undefined }),
      replace: true,
    });
  }, [navigate]);

  const recordViewerProps = project && {
    projectId,
    collectionId: reportId,
    project,
    record: openRecord,
    onClose: closeRecord,
    onNavigate: stepToRecord,
    onUnsavedChangesChange: setHasUnsavedChanges,
  };

  return (
    <div ref={rootRef} className="relative flex h-full overflow-hidden">
      {/* Stays laid out under an item shown in place — covered, not hidden —
          so closing it returns to the report where it was scrolled, without a
          reload. `inert` keeps focus and screen readers off it under an issue;
          the record view is a dialog of its own and handles both. */}
      <div
        className="flex min-w-0 flex-1 flex-col overflow-hidden"
        inert={(openIssueId !== null && isInPlace) || undefined}
      >
        {project && (
          <CollectionDetailView
            projectId={projectId}
            collectionId={reportId}
            project={project}
            onOpenIssue={openIssue}
            activeIssueId={isSplit ? openIssueId : null}
            // With the panel open the report is too narrow for six columns.
            forceIssueCards={isSplit}
            statusFilter={
              collection?.kind === "issues" ? (search.status ?? null) : null
            }
            onStatusFilterChange={setStatus}
            onOpenRecord={openRecordFromReport}
            activeRecordId={isSplit ? (openRecord?.id ?? null) : null}
          />
        )}
      </div>

      {openIssueId !== null && (isSplit || isInPlace) && (
        <div
          data-slot={isSplit ? "issue-side-panel" : "issue-full-view"}
          style={isSplit ? { width: sidePanelWidth(availableWidth) } : undefined}
          className={cn(
            "flex h-full flex-col overflow-hidden bg-background",
            isSplit
              ? "shrink-0 border-l border-border"
              : "absolute inset-0 z-10",
          )}
        >
          {/* Keyed so a sheet or confirm left open never carries to the next
              issue. On a phone the issue is a level down, so it gets a back
              arrow where the report's own sat, not the panel's Close, and the
              report's name beside it so the arrow says where it goes. */}
          <IssueDetail
            key={openIssueId}
            projectId={projectId}
            issueId={openIssueId}
            onClose={closeIssue}
            backLabel={isDesktop ? undefined : "Back to report"}
            backTitle={isDesktop ? undefined : collection?.name}
          />
        </div>
      )}

      {/* The record panel has the Photos page's fixed width per breakpoint;
          the report takes the rest. See [[records-view.spec]] US-006. */}
      {isPhotos && recordViewerProps && isSplit && (
        <div
          style={{ width: sidePanelWidth(availableWidth) }}
          className="flex h-full shrink-0 flex-col border-l border-border"
        >
          <ReportRecordViewer {...recordViewerProps} presentation="panel" />
        </div>
      )}

      {/* Phones and narrow windows: the record view's own full-screen
          dialog, mounted the way the Photos page mounts it. */}
      {isPhotos && recordViewerProps && !isSplit && (
        <ReportRecordViewer
          {...recordViewerProps}
          presentation={layout === "fullscreen" ? "fullscreen" : "dialog"}
        />
      )}

      <AlertDialog
        open={pendingRecordId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRecordId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              Unsaved changes will be discarded. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Go back</AlertDialogCancel>
            <AlertDialogAction
              className="h-12"
              onClick={() => {
                if (pendingRecordId) showRecord(pendingRecordId);
                setPendingRecordId(null);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
